import { fetchJSON as fetchAPI } from './client';

export type Dependency = { key: string; id: string; name: string; type: string; status: string; description?: string };
export type Objective = { key: string; name: string; category: string; description?: string; mandatory: boolean };
export type DNADomainSpec = { key: string; name: string; description?: string; fields?: string[]; status: string; versioned?: boolean };
export type ProviderInterface = { key: string; name: string; interface: string; description?: string; status: string };
export type FrontendFeature = { key: string; name: string; category: string; description?: string; status: string };
export type DatabaseTable = { key: string; name: string; service: string; description?: string; has_rls: boolean; partitioned?: boolean };
export type BackendAPI = { key: string; method: string; path: string; description?: string; auth_required: boolean };
export type BackgroundJob = { key: string; name: string; schedule?: string; description?: string; status: string };
export type PerformanceBudget = { key: string; surface: string; target: string; unit: string; description?: string };
export type QualityGate = { key: string; name: string; command?: string; description?: string; blocking: boolean };
export type OutOfScopeItem = { key: string; name: string; reason?: string };
export type SuccessCriterion = { key: string; criterion: string; validation?: string; mandatory: boolean };

export type SprintDashboard = {
  sprint_id: string; tenant_id: string; version: string; priority: string; applies_to: string[];
  dependencies: number; objectives: number; dna_domains: number; provider_interfaces: number;
  frontend_features: number; database_tables: number; backend_apis: number; background_jobs: number;
  performance_budgets: number; quality_gates: number; out_of_scope: number; success_criteria: number; generated_at: string;
};

export type SprintValidation = {
  valid: boolean; every_asin_has_dna: boolean; dna_versions_immutable: boolean;
  snapshots_generated: boolean; scores_generated: boolean; benchmarks_operational: boolean;
  events_published: boolean; all_apis_documented: boolean; tests_pass: boolean; ci_passes: boolean;
  performance_budgets_achieved: boolean; gaps?: string[]; evaluated_at: string;
};

export type Dev105Status = {
  sprint_id: string; version: string; priority: string; dna_domains: number;
  provider_interfaces: number; background_jobs: number; quality_gates: number;
  digital_product_twin: boolean; production_ready: boolean; provider_driven: boolean; status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';
const fetchG = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);

export const marketplaceDNAEngineDev105Api = {
  dashboard: () => fetchG<SprintDashboard>('/dev105/dashboard'),
  dependencies: () => fetchG<{ dependencies: Dependency[] }>('/dev105/dependencies').then((r) => r.dependencies),
  objectives: () => fetchG<{ objectives: Objective[] }>('/dev105/objectives').then((r) => r.objectives),
  dnaDomains: () => fetchG<{ domains: DNADomainSpec[] }>('/dev105/dna-domains').then((r) => r.domains),
  serviceArchitecture: () => fetchG<{ providers: ProviderInterface[] }>('/dev105/service-architecture').then((r) => r.providers),
  frontendFeatures: () => fetchG<{ features: FrontendFeature[] }>('/dev105/frontend-features').then((r) => r.features),
  databaseSchema: () => fetchG<{ tables: DatabaseTable[] }>('/dev105/database-schema').then((r) => r.tables),
  backendAPIs: () => fetchG<{ apis: BackendAPI[] }>('/dev105/backend-apis').then((r) => r.apis),
  backgroundJobs: () => fetchG<{ jobs: BackgroundJob[] }>('/dev105/background-jobs').then((r) => r.jobs),
  performance: () => fetchG<{ budgets: PerformanceBudget[] }>('/dev105/performance').then((r) => r.budgets),
  qualityGates: () => fetchG<{ gates: QualityGate[] }>('/dev105/quality-gates').then((r) => r.gates),
  outOfScope: () => fetchG<{ items: OutOfScopeItem[] }>('/dev105/out-of-scope').then((r) => r.items),
  successCriteria: () => fetchG<{ criteria: SuccessCriterion[] }>('/dev105/success-criteria').then((r) => r.criteria),
  validate: (attrs: Record<string, boolean>) =>
    fetchG<SprintValidation>('/dev105/validate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attrs }) }),
  status: () => fetchG<Dev105Status>('/dev105/status'),
};
